import moment from 'moment'
import api from '@/api'
import { getEnvValue } from '@/services/env-service.js'
import searchService from '@/services/search-service'
import {
  APIV2RequestDatetimeFormat,
  APIV3RequestDatetimeFormat,
} from '@/config/simpliciti-apis'

const API_CHRONO_IMPUTATIONS_URI_V2 =
  getEnvValue('VITE_API_CHRONO_IMPUTATIONS_URI') ||
  '/v2/historique/imputation_chrono'

const BADGE_OFF_ID = getEnvValue('VITE_BADGE_OFF_ID') || 65536

/**
 * Used by Location module - Chrono tab
 * Used by Diagnostics module
 * @param {String|Date|Moment} params.date Date or Moment
 * @param {String} params.vehicleId Vehicle identifier
 * @param {Date|Moment} params.startDate
 * @param {Date|Moment} params.endDate
 * @param {Function} params.apiHandler (unit-test only)
 * @param {'v2'|'v3'} apiVersion Api version used
 */
export async function getVehicleChrono(params = {}, apiVersion = 'v2') {
  if (!params.vehicleId) {
    throw new Error('Required:  vehicleId')
  }

  if (!params.date && (!params.startDate || !params.endDate)) {
    throw new Error(
      `Required:  At least date or startDate/endDate must be specified (Params: ${JSON.stringify(
        params
      )})`
    )
  }

  if (params.date && params.startDate && params.endDate) {
    throw new Error(
      `Required:  Only date or startDate/endDate must be specified (Params: ${JSON.stringify(
        params
      )})`
    )
  }

  if (
    (params.startDate && !params.endDate) ||
    (!params.startDate && params.endDate)
  ) {
    throw new Error('Required:  Both startDate/endDate must be specified')
  }

  if (!['v2', 'v3'].includes(apiVersion)) {
    throw new Error(
      `Required:  ApiVersion must be "v2" or "v3", provided: "${apiVersion}"`
    )
  }

  let startDate = params.startDate ? params.startDate : moment(params.date)
  startDate = startDate._isAMomentObject ? startDate : moment(startDate)

  let endDate = params.endDate ? params.endDate : params.date
  endDate = endDate._isAMomentObject ? endDate : moment(endDate)

  const apiRequestDatetimeFormat =
    apiVersion === 'v2'
      ? APIV2RequestDatetimeFormat
      : APIV3RequestDatetimeFormat

  if (params.date && !params.startDate && !params.endDate) {
    const at = params.startDate ? params.startDate : moment(params.date)

    let startRangeSelected = searchService.getRangeTimeSelected(at).start
    let endRangeDateSelected = searchService.getRangeTimeSelected(at).end

    startDate = moment(startRangeSelected).format(apiRequestDatetimeFormat)
    endDate = moment(endRangeDateSelected).format(apiRequestDatetimeFormat)
  } else {
    startDate = startDate.format(apiRequestDatetimeFormat)
    endDate = endDate.format(apiRequestDatetimeFormat)
  }

  console.debugVerboseScope(5, 'chrono', 'getVehicleChrono', {
    startDate,
    endDate,
    vehicleId: params.vehicleId,
    params,
  })

  if (apiVersion === 'v2') {
    const apiURL = `${API_CHRONO_IMPUTATIONS_URI_V2}/${params.vehicleId}/${startDate}/${endDate}/true`

    const apiHandler = params.apiHandler
      ? () => params.apiHandler(apiURL)
      : () => api.v2.get(apiURL)

    return normalizeChronoResponse(((await apiHandler()) || {}).data || {})
  }

  if (apiVersion === 'v3') {
    const data = await api.tachographEvents.getAll({
      vehicleId: params.vehicleId,
      start: startDate,
      end: endDate,
      withSynthesis: true,
    })

    if (data.length > 0) {
      return data[0]
    }

    return {}
  }
}

export function getChronoIconPath(imputationType) {
  let iconPath = ''

  if (imputationType) {
    switch (imputationType.toLowerCase()) {
      case 'conduite':
        iconPath = './lib/realtimeMap/assets/picto_chrono/chrono_4.svg'
        break
      case 'travail':
        iconPath = './lib/realtimeMap/assets/picto_chrono/chrono_3.svg'
        break
      case 'dispo':
        iconPath = './lib/realtimeMap/assets/picto_chrono/chrono_2.svg'
        break
      case 'repos':
        iconPath = './lib/realtimeMap/assets/picto_chrono/chrono_1.svg'
        break
      default:
        iconPath = './lib/realtimeMap/assets/picto_chrono/chrono_1.svg'
        break
    }
  }

  return iconPath
}

function normalizeChronoResponse(data, version = 'v2') {
  console.debugVerbose(5, 'normalizeChronoResponse', {
    data,
  })

  if (!data) {
    return {}
  }

  return {
    synthesis: normalizeChronoResponseSynthesis(data, version),
    details: normalizeChronoResponseDetails(data),
  }
}

function formatObjectValue(item, value) {
  return item.hasOwnProperty(value) ? item[value] : ''
}

function formatArrayValue(array, value) {
  if (typeof array[value] === 'number') {
    return array[value]
  }

  return array[value] ? array[value] : ''
}

function formatObjectAddress(item) {
  return item.hasOwnProperty('rue') &&
    item.hasOwnProperty('cp') &&
    item.hasOwnProperty('ville')
    ? item['rue'] + ' ' + item['cp'] + ' ' + item['ville']
    : ''
}

export function normalizeChronoResponseSynthesis(item, version = 'v2') {
  let synthesis = {}

  //Get correct key from version
  const key = version === 'v3' ? 'synthesis' : 'synthese'

  //Get value keys for each version
  const valueKeys = getSynthesisValueKeys()

  if (item && item.hasOwnProperty(key)) {
    synthesis = Object.fromEntries(
      Object.entries(valueKeys[version]).map(([k, v]) => [
        k,
        formatObjectValue(item[key], v),
      ])
    )
  }

  return synthesis
}

export function getChronoItemType(type = 'unknown') {
  const table = {
    travail: 'working',
    repos: 'resting',
    conduite: 'driving',
    dispo: 'available',
    unknown: 'unknown',
  }
  if (!table[type.toLowerCase()]) {
    console.error('Invalid chrono type: ' + table[type.toLowerCase()])
  }
  return table[type.toLowerCase()]
}

export function getChronoColorFromType(type) {
  const table = {
    working: getEnvValue('VITE_CHRONO_STATUS_COLOR_WORKING', '#1D8CC3'),
    resting: getEnvValue('VITE_CHRONO_STATUS_COLOR_RESTING', '#418339'),
    driving: getEnvValue('VITE_CHRONO_STATUS_COLOR_DRIVING', '#CC494D'),
    available: getEnvValue('VITE_CHRONO_STATUS_COLOR_AVAILABLE', '#ECC646'),
    unknown: getEnvValue('VITE_CHRONO_STATUS_COLOR_UNKNOWN', 'black'),
  }
  return table[type]
}

/**
 * @todo Map type to custom values ['driving', 'available', 'resting', 'working'],
 * @param {*} item
 * @returns
 */
function normalizeChronoResponseDetails(item) {
  let details = []

  if (item && item.hasOwnProperty('imputations')) {
    item.imputations.forEach((element) => {
      let normalizedType = getChronoItemType(element.imputation_type)
      details.push({
        startDate: formatArrayValue(element, 'dateheure_debut'),
        endDate: formatArrayValue(element, 'dateheure_fin'),
        period: formatArrayValue(element, 'duree'),
        type: formatArrayValue(element, 'imputation_type'),
        typeAlt: normalizedType,
        color: getChronoColorFromType(normalizedType),
        iconPath: getChronoIconPath(
          formatArrayValue(element, 'imputation_type')
        ),
        distance: formatArrayValue(element, 'distance'),
        service: formatArrayValue(element, 'cumul_service'),
        driverName: formatArrayValue(element, 'chauffeur_nom'),
        address: formatObjectAddress(element),
        lat: formatArrayValue(element, 'latitude'),
        lng: formatArrayValue(element, 'longitude'),
        periodDrive: formatArrayValue(element, 'duree_conduite'),
        periodWork: formatArrayValue(element, 'duree_travail'),
        periodRest: formatObjectValue(element, 'duree_repos'),
        periodAvailable: formatObjectValue(element, 'duree_dispo'),
        zone: formatObjectValue(element, 'zone'),
        speed: formatObjectValue(element, 'vitesse'),
      })
    })
  }

  return details
}

export function getSynthesisValueKeys() {
  return {
    v2: {
      startDay: 'dh_debut_journee',
      endDay: 'dh_fin_journee',
      amplitude: 'amplitude',
      nbStop: 'nb_arret_superieur_10mn',
      distance: 'distance',
      speed: 'vitesse',
      periodService: 'duree_service',
      periodDrive: 'duree_conduite',
      percentDrive: 'pourcentage_conduite',
      periodAvailable: 'duree_dispo',
      percentAvailable: 'pourcentage_dispo',
      periodWork: 'duree_travail',
      percentWork: 'pourcentage_travail',
      periodWorkAvailable: 'duree_dispo_travail',
      percentWorkAvailable: 'pourcentage_dispo_travail',
      maxPeriodRest: 'duree_max_repos',
      periodRest: 'duree_repos',
      percentRest: 'pourcentage_repos',
      maxPeriodStart: 'heuredebut_duree_max_repos',
    },
    v3: {
      startDay: 'startDay',
      endDay: 'endDay',
      amplitude: 'amplitude',
      nbStop: 'nbStop',
      distance: 'distance',
      speed: 'speed',
      periodService: 'periodService',
      periodDrive: 'periodDrive',
      percentDrive: 'percentDrive',
      periodAvailable: 'periodAvailable',
      percentAvailable: 'percentAvailable',
      periodWork: 'periodWork',
      percentWork: 'percentWork',
      periodWorkAvailable: 'periodWorkAvailable',
      percentWorkAvailable: 'percentWorkAvailable',
      maxPeriodRest: 'maxPeriodRest',
      periodRest: 'periodRest',
      percentRest: 'percentRest',
      maxPeriodStart: 'maxPeriodStart',
    },
  }
}
